import { useCallback, useContext, type FC, type ReactNode } from "react";
import { FilterContext } from "./filterContext.js";
import { DEFAULT_FILTER_PARAMS, type FilterParams } from "./filterParams.js";
import { filterRenderers } from "./filterRenderers.js";
import type { FilterKey, FilterValues } from "./filterState.js";

const EMPTY_FILTER_KEYS: readonly FilterKey[] = Object.freeze([]);

export interface FilterProps<F extends FilterKey> {
  filter: F;
  params?: FilterParams;
  /**
   * Every key the page opted into. A widget that spans more than its own key
   * needs this to know whether the neighbouring filter is actually in play —
   * without it the date chip would offer a basis switcher on pages that never
   * send one.
   */
  filterKeys?: readonly FilterKey[];
}

type FilterComponent = <K extends FilterKey>(
  props: FilterProps<K>,
) => ReactNode;

/** Renders a widget for the filter identified by the given key. */
export const Filter: FC<FilterProps<FilterKey>> & FilterComponent = <
  K extends FilterKey,
>(
  props: FilterProps<K>,
) => {
  const { values, onChange } = useContext(FilterContext);

  const render = filterRenderers[props.filter];
  const value = values[props.filter];
  const onValueChange = useCallback(
    (newValue: FilterValues[K]) => {
      onChange({ [props.filter]: newValue });
    },
    [onChange, props.filter],
  );

  return render({
    params: props.params ?? DEFAULT_FILTER_PARAMS,
    filterKeys: props.filterKeys ?? EMPTY_FILTER_KEYS,
    value,
    onChange: onValueChange,
  });
};
